<template>
  <!-- Transfer Dialog als Bootstrap Modal -->
  <Teleport to="#modalTarget">
    <div class="modal quitly-modal d-block" tabindex="-1" aria-hidden="false" v-if="show" style="background-color: rgba(0,0,0,0.5);" @click.self="$emit('close')">
      <div class="modal-dialog quitly-modal-dialog modal-lg" @click.stop>
        <div class="modal-content quitly-modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ dialogTitle }}</h5>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close">&nbsp;</button>
        </div>
        <div class="modal-body">
          <div class="container-fluid">
            <!-- Transfer Type Selection -->
            <div class="row mb-4">
              <div class="col-6">
                <button
                  @click="transferType = 'personal_to_team'"
                  :class="[
                    'btn w-100 p-3',
                    transferType === 'personal_to_team' 
                      ? 'btn-primary' 
                      : 'btn-outline-secondary'
                  ]"
                >
                  <div class="text-center">
                    <div class="h4 mb-2">
                      <font-awesome-icon icon="arrow-up" class="text-primary me-2" />
                      <font-awesome-icon icon="users" class="text-info" />
                    </div>
                    <div class="fw-bold">Personal → Bereich</div>
                    <div class="small text-muted">
                      Quittungen an Bereich übertragen
                    </div>
                  </div>
                </button>
              </div>
              
              <div class="col-6">
                <button
                  @click="transferType = 'team_to_personal'"
                  :class="[
                    'btn w-100 p-3',
                    transferType === 'team_to_personal' 
                      ? 'btn-primary' 
                      : 'btn-outline-secondary'
                  ]"
                >
                  <div class="text-center">
                    <div class="h4 mb-2">
                      <font-awesome-icon icon="users" class="text-info me-2" />
                      <font-awesome-icon icon="arrow-down" class="text-success" />
                    </div>
                    <div class="fw-bold">Bereich → Personal</div>
                    <div class="small text-muted">
                      Quittungen zurückholen
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <!-- Account Information -->
            <div class="row mb-4">
              <div class="col-6">
                <div class="card">
                  <div class="card-body text-center">
                    <div class="small text-muted">Personal Account</div>
                    <div class="h4 fw-bold text-success">
                      {{ personalCoins }} {{ personalCoins === 1 ? 'Quittung' : 'Quittungen' }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-6">
                <div class="card">
                  <div class="card-body text-center">
                    <div class="small text-muted">Bereich Account</div>
                    <div class="h4 fw-bold text-primary">
                      {{ teamCoins }} {{ teamCoins === 1 ? 'Quittung' : 'Quittungen' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Available for Transfer (nur bei team_to_personal) -->
            <div v-if="transferType === 'team_to_personal'" class="alert alert-gradient-warning mb-4">
              <div class="small mb-1">
                <strong>Verfügbar für Rücktransfer</strong>
              </div>
              <div class="h5 fw-bold">
                {{ availableForTransfer }} {{ availableForTransfer === 1 ? 'Quittung' : 'Quittungen' }}
              </div>
              <div class="small">
                Nur unverbrauchte Quittungen können zurücktransferiert werden
              </div>
            </div>

            <!-- Amount Input -->
            <div class="mb-4">
              <label class="form-label fw-bold">Anzahl {{ amount === 1 ? 'Quittung' : 'Quittungen' }}</label>
              <div class="input-group">
                <input
                  v-model.number="amount"
                  type="number"
                  min="1"
                  :max="maxAmount"
                  class="form-control form-control-lg"
                  placeholder="Anzahl eingeben..."
                />
                <span class="input-group-text">{{ amount === 1 ? 'Quittung' : 'Quittungen' }}</span>
              </div>
              <div class="form-text">
                Maximum: {{ maxAmount }} {{ maxAmount === 1 ? 'Quittung' : 'Quittungen' }}
              </div>
            </div>

            <!-- Quick Amount Buttons -->
            <div class="d-flex gap-2 mb-4">
              <button
                v-for="quickAmount in quickAmounts"
                :key="quickAmount"
                @click="amount = quickAmount"
                :disabled="quickAmount > maxAmount"
                class="btn btn-sm btn-outline-secondary"
              >
                {{ quickAmount }}
              </button>
              <button
                @click="amount = maxAmount"
                :disabled="maxAmount === 0"
                class="btn btn-sm btn-outline-secondary"
              >
                Max
              </button>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Abbrechen
          </button>
          <button
            type="button"
            @click="handleTransfer"
            :disabled="!canTransfer || loading"
            class="btn btn-primary d-flex align-items-center gap-2"
          >
            <div v-if="loading" class="spinner-border spinner-border-sm" role="status"></div>
            <span>{{ loading ? 'Übertrage...' : 'Transfer starten' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>  </Teleport>
</template>

<script setup>
import { ref, computed, watch, toRefs } from 'vue'
import { coinTransferApi } from '../../api/coinTransfer'
import { useNotification } from '../../services/notification'

const emit = defineEmits(['close', 'transfer-completed'])
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  teamId: {
    type: String,
    required: true
  },
  personalCoins: {
    type: Number,
    default: 0
  },
  teamCoins: {
    type: Number,
    default: 0
  }
})

const { show, teamId, personalCoins, teamCoins } = toRefs(props)

const transferType = ref('personal_to_team')
const amount = ref(null)
const loading = ref(false)
const error = ref(null)
const availableForTransfer = ref(0)
const quickAmounts = ref([10, 25, 50, 100])

const dialogTitle = computed(() =>
  transferType.value === 'personal_to_team'
    ? 'Quittungen an Bereich übertragen'
    : 'Quittungen zurückholen'
)

const maxAmount = computed(() =>
  transferType.value === 'personal_to_team'
    ? personalCoins.value
    : availableForTransfer.value
)

const canTransfer = computed(() =>
  amount.value &&
  amount.value > 0 &&
  amount.value <= maxAmount.value &&
  !loading.value
)

function resetForm() {
  amount.value = null
  error.value = null
  loading.value = false
  transferType.value = 'personal_to_team'
  availableForTransfer.value = 0
}

async function loadAvailableCoins() {
  try {
    const response = await coinTransferApi.getAvailableCoins(teamId.value)
    availableForTransfer.value = response.totalAvailable
  } catch (err) {
    console.error('Fehler beim Laden verfügbarer Quittungen:', err)
    error.value = 'Fehler beim Laden verfügbarer Quittungen'
  }
}

watch(show, (newVal) => {
  if (newVal) {
    resetForm()
    if (transferType.value === 'team_to_personal') {
      loadAvailableCoins()
    }
  }
})

watch(transferType, () => {
  amount.value = null
  error.value = null
  if (transferType.value === 'team_to_personal') {
    loadAvailableCoins()
  }
})

async function handleTransfer() {
  if (!canTransfer.value) {
    return
  }

  loading.value = true
  error.value = null

  try {
    let result
    if (transferType.value === 'personal_to_team') {
      result = await coinTransferApi.transferToTeam(teamId.value, amount.value)
    } else {
      result = await coinTransferApi.transferToPersonal(teamId.value, amount.value)
    }

    const { showSuccess } = useNotification()
    showSuccess(`${amount.value} ${amount.value === 1 ? 'Quittung' : 'Quittungen'} erfolgreich übertragen!`)

    emit('transfer-completed', {
      type: transferType.value,
      amount: amount.value,
      result
    })

    emit('close')
  } catch (err) {
    console.error('Transfer-Fehler:', err)
    console.error('Error details:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      message: err.message
    })

    error.value = err.response?.data?.message || err.message || 'Transfer fehlgeschlagen'
  } finally {
    loading.value = false
  }
}
</script>

